import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { qualityGateTone, type QualityScore } from './qualityGate'

function badgeStyle(color: string): CSSProperties {
  return {
    color,
    borderColor: color,
    backgroundColor: `color-mix(in oklch, ${color} 12%, transparent)`,
  }
}

export function QualityScoreBadge({
  quality,
  compact,
  className,
}: {
  quality?: QualityScore | null
  compact?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  if (!quality) {
    return (
      <Badge variant="outline" className={cn('h-5 rounded-md px-1.5 text-xs font-normal', className)}>
        {t('qualityGate.unscored')}
      </Badge>
    )
  }
  if (quality.assessmentError) {
    // 失败响亮化: 评分后端失败时显式提示 (而非与「尚未评分」无差别), reason 进 title 供排障。
    const failedColor = 'var(--gate-review)'
    const failedTitle = quality.assessmentError.reason
      ? `${t('qualityGate.assessmentFailed')} · ${quality.assessmentError.reason}`
      : t('qualityGate.assessmentFailed')
    return (
      <Badge
        variant="outline"
        className={cn('h-5 gap-1.5 rounded-md px-1.5 text-xs font-medium', className)}
        style={badgeStyle(failedColor)}
        title={failedTitle}
        aria-label={failedTitle}
      >
        <span className="size-1.5 rounded-full" style={{ background: failedColor }} />
        {t('qualityGate.assessmentFailed')}
      </Badge>
    )
  }
  const tone = qualityGateTone(quality.score)
  const title = `${t(tone.labelKey)} · ${t('qualityGate.scoreValue', { score: quality.score })}`
  return (
    <Badge
      variant="outline"
      className={cn('h-5 gap-1.5 rounded-md px-1.5 text-xs font-medium', className)}
      style={badgeStyle(tone.color)}
      title={title}
      aria-label={title}
    >
      <span className="size-1.5 rounded-full" style={{ background: tone.color }} />
      {compact ? quality.score : t('qualityGate.scoreShort', { score: quality.score })}
    </Badge>
  )
}
